'use strict';
// Strict-contract normalization; legacy preparation retains its nonempty guard.

const MONTH_SHORT=['Jan','Feb','Mar','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Des'];
const ApiTransform={
 transformSaldo:rows=>rows.map(r=>({id:String(r.n),accNum:Number(r.n),name:r.name||'',accName:r.name||'',ib:Number(r.ib),change:Number(r.ch),ub:Number(r.ub)})),
 transformResultat:(rows,months)=>rows.map(r=>({id:String(r.n),accNum:Number(r.n),name:r.name||'',accName:r.name||'',sum:Number(r.sum),change:Number(r.ch??r.sum),months:Object.fromEntries((months||Object.keys(r.months)).map(m=>[m,Number(r.months[m]??0)]))})),
 transformPreviousYear:(r,months)=>r?{year:Number.isInteger(r.year)&&r.year>0?r.year:null,saldo:ApiTransform.transformSaldo(r.saldo),saldoEndMonth:r.saldoEndMonth,resultat:ApiTransform.transformResultat(r.resultat,months),resultatFullYear:Array.isArray(r.resultatFullYear)?ApiTransform.transformResultat(r.resultatFullYear):r.startMonth===1&&r.endMonth===12?ApiTransform.transformResultat(r.resultat):null}:null
};


const fail = (pl, no) => {
    const error = new Error(pl);
    error.code = 'AUDIT_DATA_INVALID';
    throw error;
};
const nameKey = value => typeof value === 'string' ? value.normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase() : '';
const number = value => {
    if (!['number', 'string'].includes(typeof value) || String(value).trim() === '' || !Number.isFinite(Number(value))) {
        fail('Finago: brak poprawnej kwoty lub numeru konta. Audyt nie został obliczony.',
            'Finago: ugyldig beløp eller kontonummer. Kontrollen er ikke beregnet.');
    }
    return Number(value);
};
const cents = value => {
    const result = Math.round(number(value) * 100);
    if (!Number.isSafeInteger(result)) fail('Finago: kwota poza zakresem.', 'Finago: beløpet er utenfor gyldig område.');
    return result;
};
const account = value => {
    const n = number(value);
    if (!Number.isInteger(n) || n < 1000 || n > 8999) fail('Finago: nieprawidłowe konto.', 'Finago: ugyldig konto.');
    return n;
};

/**
 * Canonical RAPPORTER API contract (Finago and validated Tripletex adapter). Never apply to CSV.
 * A missing balance account in this connector has IB = YTD = period movement = 0.
 * The transaction net must independently agree before using that zero closing balance.
 * Existing rows anchor the reconstruction at UB, so partial periods never use annual IB.
 * Integer øre prevent accumulated floating point noise. No accounting records are changed.
 */
const normalizeLedger = (data, lines, expected) => {
    const year = Number(expected?.year), start = Number(expected?.monthFrom), end = Number(expected?.monthTo);
    if (!Number.isInteger(year) || year < 1900 || year > 2200 || !Number.isInteger(start) || !Number.isInteger(end)
        || start < 1 || end > 12 || start > end || !nameKey(expected?.clientName)) {
        fail('Wybierz firmę i poprawny okres audytu.', 'Velg firma og en gyldig kontrollperiode.');
    }
    for (const response of [data, lines]) {
        if (!response || nameKey(response.client) !== nameKey(expected.clientName)) {
            fail('Finago: nie można potwierdzić firmy w odpowiedzi. Audyt został zatrzymany.',
                'Finago: firmaet i svaret kan ikke bekreftes. Kontrollen er stoppet.');
        }
        if (Number(response.year) !== year || Number(response.startMonth) !== start || Number(response.endMonth) !== end) {
            fail('Finago zwróciło inny lub nieokreślony okres. Pobierz dane ponownie.',
                'Finago returnerte en annen eller ukjent periode. Hent data på nytt.');
        }
    }
    if (!Array.isArray(data.saldo) || !Array.isArray(data.resultat)
        || !Array.isArray(lines.transactions)
        || Number(lines.count) !== lines.transactions.length) {
        fail('Finago: niepełna odpowiedź z saldami lub transakcjami. Poprzedni wynik pozostaje bez zmian.',
            'Finago: ufullstendig svar med saldoer eller transaksjoner. Forrige resultat beholdes.');
    }
    const expectedNet = new Map(), closing = new Map(), names = new Map();
    for (const [rows, isBalance] of [[data.saldo, true], [data.resultat, false]]) {
        for (const row of rows) {
            const n = account(row?.n);
            if ((isBalance && n >= 3000) || (!isBalance && n < 3000) || expectedNet.has(n)) {
                fail('Finago: powtórzone konto lub błędny rodzaj zestawienia.', 'Finago: duplisert konto eller feil rapporttype.');
            }
            const net = cents(isBalance ? row.ch : row.sum);
            expectedNet.set(n, net);
            names.set(n, row.name || `Konto ${n}`);
            if (isBalance) {
                const ib = cents(row.ib);
                closing.set(n, cents(row.ub));
                if (start === 1 && ib + net !== closing.get(n)) {
                    fail(`Finago: saldo otwarcia, ruch i saldo końcowe konta ${n} są sprzeczne.`,
                        `Finago: inngående saldo, bevegelse og utgående saldo for konto ${n} stemmer ikke.`);
                }
            }
        }
    }
    const net = new Map(), monthly = new Map();
    const transactions = lines.transactions.map(tx => {
        if (tx?.fxRateMissing) fail('Finago: brakuje kursu waluty dla transakcji. Audyt został zatrzymany.',
            'Finago: valutakurs mangler for en transaksjon. Kontrollen er stoppet.');
        const n = account(tx?.accountNo), amount = cents(tx?.amount);
        const date = typeof tx?.date === 'string' ? tx.date : '';
        const match = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(date);
        const parsed = match && new Date(`${match[1]}-${match[2]}-${match[3]}T00:00:00Z`);
        const month = match ? Number(match[2]) : 0;
        if (!match || !Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date.slice(0, 10)
            || Number(match[1]) !== year || month < start || month > end || !Number.isFinite(Date.parse(date))) {
            fail('Finago: transakcja ma nieprawidłową datę lub pochodzi spoza okresu audytu.',
                'Finago: en transaksjon har ugyldig dato eller ligger utenfor kontrollperioden.');
        }
        net.set(n, (net.get(n) || 0) + amount);
        if (!monthly.has(n)) monthly.set(n, new Map());
        monthly.get(n).set(month, (monthly.get(n).get(month) || 0) + amount);
        return { ...tx, accountNo: n, amount: amount / 100 };
    });
    for (const n of new Set([...expectedNet.keys(), ...net.keys()])) {
        // Missing balance accounts are allowed only with zero period net (connector contract).
        if ((expectedNet.get(n) || 0) !== (net.get(n) || 0)) {
            fail(`Finago: suma transakcji konta ${n} nie zgadza się z zestawieniem. Pobierz dane ponownie.`,
                `Finago: transaksjonssummen for konto ${n} stemmer ikke med rapporten. Hent data på nytt.`);
        }
    }
    // Matching period totals can hide values shifted between months or malformed numbers.
    for (const row of data.resultat) {
        if (!row.months || typeof row.months !== 'object' || Array.isArray(row.months)) {
            fail('Finago: brak miesięcznych danych wyniku.', 'Finago: månedlige resultatdata mangler.');
        }
        for (let m = start; m <= end; m++) {
            const raw = Object.hasOwn(row.months, MONTH_SHORT[m - 1]) ? row.months[MONTH_SHORT[m - 1]] : 0;
            if (cents(raw) !== (monthly.get(Number(row.n))?.get(m) || 0)) {
                fail(`Finago: miesięczny wynik konta ${row.n} nie zgadza się z transakcjami.`,
                    `Finago: månedsresultatet for konto ${row.n} stemmer ikke med transaksjonene.`);
            }
        }
    }
    for (const [n, values] of monthly) {
        if (n >= 3000 && !expectedNet.has(n) && [...values.values()].some(v => v !== 0)) {
            fail(`Finago: brakuje miesięcznego wyniku konta ${n}.`, `Finago: månedsresultatet for konto ${n} mangler.`);
        }
    }
    const vatMonthly = [];
    for (const n of [...new Set([...closing.keys(), ...net.keys()])].sort((a, b) => a - b)) {
        if (n !== 1920 && (n < 2700 || n > 2749)) continue;
        let balance = (closing.get(n) || 0) - (net.get(n) || 0);
        const openingBalance = balance / 100;
        const monthlyBalance = {}, monthlyChange = {};
        for (let m = start; m <= end; m++) {
            const change = monthly.get(n)?.get(m) || 0;
            balance += change;
            monthlyBalance[MONTH_SHORT[m - 1]] = balance / 100;
            monthlyChange[MONTH_SHORT[m - 1]] = change / 100;
        }
        vatMonthly.push({ n, name: names.get(n) || data.accountNames?.[n] || `Konto ${n}`,
            monthlyBalance, monthlyChange, periodStartMonth: start, openingBalance });
    }
    const months = MONTH_SHORT.slice(start - 1, end);
    return {
        saldo: ApiTransform.transformSaldo(data.saldo).map(row => row.accNum === 1530
            ? { ...row, months: Object.fromEntries(months.map((month, index) =>
                [month, (monthly.get(1530)?.get(start + index) || 0) / 100])) }
            : row),
        resultat: ApiTransform.transformResultat(data.resultat, months),
        previousYear: ApiTransform.transformPreviousYear(data.previousYear, months),
        vatMonthly, transactions,
    };
};

module.exports={normalizeLedger};

